if (!customElements.get('fruuna-video-experiment')) {
  customElements.define(
    'fruuna-video-experiment',
    class FruunaVideoExperiment extends HTMLElement {
      constructor() {
        super();
        this.storageKey = null;
        this.variant = null;
        this.userPaused = false;
        this.publishedMilestones = new Set();
      }

      connectedCallback() {
        this.storageKey = this.dataset.storageKey;
        this.variant = this.getVariant();
        this.updateOrderAttribution();
        this.publishWhenReady('exposure');
        this.observeSuccessfulAdds();

        if (this.variant !== 'video') return;

        this.hidden = false;
        this.video = this.querySelector('video');
        this.control = this.querySelector('[data-fruuna-video-control]');
        const source = this.querySelector('[data-fruuna-video-source]');
        if (!this.video || !source) return;

        source.src = this.dataset.videoUrl;
        this.video.load();
        this.bindVideoEvents();
        this.startWhenVisible();
      }

      getVariant() {
        const override = new URLSearchParams(window.location.search).get('fruuna_video_test');
        if (override === 'control' || override === 'video') return override;

        try {
          const savedVariant = window.localStorage.getItem(this.storageKey);
          if (savedVariant === 'control' || savedVariant === 'video') return savedVariant;

          const variant = this.randomNumber() < 0.5 ? 'control' : 'video';
          window.localStorage.setItem(this.storageKey, variant);
          return variant;
        } catch (error) {
          return this.randomNumber() < 0.5 ? 'control' : 'video';
        }
      }

      randomNumber() {
        if (window.crypto?.getRandomValues) {
          const values = new Uint32Array(1);
          window.crypto.getRandomValues(values);
          return values[0] / 4294967296;
        }
        return Math.random();
      }

      updateOrderAttribution() {
        document.querySelectorAll('[data-fruuna-video-test-property]').forEach((input) => {
          input.value = this.variant;
        });
      }

      bindVideoEvents() {
        this.control?.addEventListener('click', () => {
          if (this.video.paused) {
            this.userPaused = false;
            this.video.play().catch(() => {});
          } else {
            this.userPaused = true;
            this.video.pause();
          }
        });
        this.video.addEventListener('play', () => {
          this.updateControl(true);
          this.publishMilestone('video_start');
        });
        this.video.addEventListener('pause', () => this.updateControl(false));
        this.video.addEventListener('timeupdate', () => {
          if (!this.video.duration) return;
          const progress = this.video.currentTime / this.video.duration;
          if (progress >= 0.5) this.publishMilestone('video_50');
          if (progress >= 0.95) this.publishMilestone('video_complete');
        });
      }

      updateControl(isPlaying) {
        if (!this.control) return;
        this.control.dataset.state = isPlaying ? 'playing' : 'paused';
        this.control.setAttribute(
          'aria-label',
          isPlaying ? this.control.dataset.pauseLabel : this.control.dataset.playLabel
        );
      }

      startWhenVisible() {
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting && !this.userPaused) {
                this.video.play().catch(() => {});
              } else {
                this.video.pause();
              }
            });
          },
          { threshold: 0.5 }
        );
        observer.observe(this.video);
      }

      observeSuccessfulAdds() {
        if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
        subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event?.source === 'product-form') this.publishWhenReady('add_to_cart');
        });
      }

      publishMilestone(eventType) {
        if (this.publishedMilestones.has(eventType)) return;
        this.publishedMilestones.add(eventType);
        this.publishWhenReady(eventType);
      }

      publishWhenReady(eventType, attempt = 0) {
        if (window.Shopify?.analytics?.publish) {
          window.Shopify.analytics.publish('fruuna:product_video_experiment', {
            event_type: eventType,
            experiment_id: this.dataset.experimentId,
            variant: this.variant,
            product_id: this.dataset.productId,
            product_handle: this.dataset.productHandle,
          });
          return;
        }

        if (attempt < 10) {
          window.setTimeout(() => this.publishWhenReady(eventType, attempt + 1), 250);
        }
      }
    }
  );
}
